import { addDisplayName, addProps, branch, flowMax, returns } from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { ProductUrl } from 'shared/src/api/types'
import { format, isSameDay } from 'date-fns'
import { TFunction } from 'i18next'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Modal } from '../Modal'
import { IconButton } from '../IconButton'
import { LABELS_STYLE, MAIN_TITLE_STYLE, NOTES_BOLD_STYLE } from '../styles'
import Icon from '../Icon'
import { Product } from './Product'

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    paddingLeft: 21,
    paddingRight: 24,
    paddingTop: 19,
    height: '100dvh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 41,
  },
  title: {
    ...MAIN_TITLE_STYLE,
    color: '#232323',
  },
  closeButton: {
    position: 'relative',
    right: -10,
  },
  closeButtonIcon: {
    color: '#90734F',
  },
  queueContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    paddingBottom: 24,
  },
  urlContainer: {
    backgroundColor: 'transparent',
    height: 64,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  textContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    overflow: 'hidden',
  },
  url: {
    ...NOTES_BOLD_STYLE,
    color: '#363432',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  date: {
    ...LABELS_STYLE,
    color: '#363432',
  },
  chevron: {
    color: '#CBA67A',
    flexShrink: 0,
  },
  productContainer: {
    backgroundColor: 'transparent',
    flexShrink: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
})

const formattedDate = (date: Date, t: TFunction) =>
  isSameDay(date, new Date())
    ? t('admin.post.productQueueTodayDate', { date: format(date, 'h:mma') })
    : format(date, 'M/d h:mma')

interface ProductUrlRowProps {
  productUrl: ProductUrl
  onClick: () => void
}

export const ProductUrlRow: FC<ProductUrlRowProps> = flowMax(
  addDisplayName('ProductUrlRow'),
  addProps(() => ({
    classes: useStyles(),
  })),
  branch(
    ({ productUrl: { product } }) => !!product,
    returns(({ productUrl: { product }, onClick, classes }) => (
      <Product
        product={product!}
        onClick={onClick}
        className={classes.productContainer}
      />
    ))
  ),
  addTranslationHelpers,
  addProps(({ productUrl: { createdAt }, t }) => ({
    dateFormatted: formattedDate(createdAt, t),
  })),
  ({ productUrl: { url }, dateFormatted, onClick, classes }) => (
    <button className={classes.urlContainer} onClick={onClick}>
      <div className={classes.textContainer}>
        <div className={classes.url}>{url}</div>
        <div className={classes.date}>{dateFormatted}</div>
      </div>
      <Icon name="chevron-right" className={classes.chevron} />
    </button>
  )
)

interface Props {
  queue: ProductUrl[]
  onClose: () => void
  onUrlChoose: (productUrl: ProductUrl) => void
}

export const ProductQueueModal: FC<Props> = flowMax(
  addDisplayName('ProductQueueModal'),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({ queue, onClose, onUrlChoose, classes, t }) => (
    <Modal containerClassName={classes.container}>
      <div className={classes.header}>
        <h1 className={classes.title}>{t('admin.post.productQueueTitle')}</h1>
        <IconButton
          onClick={onClose}
          name="close"
          className={classes.closeButton}
          iconClassName={classes.closeButtonIcon}
          addHitSlop
        />
      </div>
      <div className={classes.queueContainer}>
        {queue.map((productUrl) => (
          <ProductUrlRow
            productUrl={productUrl}
            onClick={() => onUrlChoose(productUrl)}
            key={productUrl.id}
          />
        ))}
      </div>
    </Modal>
  )
)
